import { useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { toast } from "react-toastify";
import Button from "../../componentes/button/button";
import { opcoesDeCatalogo } from "./useOpcoesInventario";
import SeletorResponsavel from "./SeletorResponsavel";
import { CAMPOS_MAC, campoVisivel, exibirCampo, macCompleto, mascaraMac, vazio } from "./tiUtils";

const INPUT = "border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground w-full";

/** Explicações curtas exibidas sob alguns campos, para diferenciar identificadores parecidos. */
const AJUDA_CAMPOS = {
    nomenclatura: "Nome do equipamento no inventário/rede (ex.: NB-TI-001).",
    patrimonio: "Número da plaqueta (etiqueta) do controle patrimonial da empresa, colada no equipamento. "
        + "Não é o número de série do fabricante.",
    numeroSerie: "Número de série impresso pelo fabricante no equipamento.",
};

/** Sugere o tipo de computador quando o tipo de dispositivo escolhido já indica Desktop ou Notebook. */
function tipoComputadorSugerido(nomeTipo) {
    const n = (nomeTipo || "").toLowerCase();
    if (n.includes("notebook") || n.includes("laptop")) return "NOTEBOOK";
    if (n.includes("desktop")) return "DESKTOP";
    return null;
}

/** Busca de ativo (equipamento principal) com pesquisa no servidor. */
export function SeletorAtivo({ service, valorId, descricao, excluirId, onChange, disabled }) {
    const [termo, setTermo] = useState("");
    const [resultados, setResultados] = useState([]);

    useEffect(() => {
        if (termo.trim().length < 2) {
            setResultados([]);
            return undefined;
        }
        const espera = setTimeout(async () => {
            const res = await service.buscarParaVinculo(termo.trim(), excluirId);
            setResultados(res.data || []);
        }, 300);
        return () => clearTimeout(espera);
    }, [termo, service, excluirId]);

    if (valorId) {
        return (
            <div className="flex items-center gap-2 text-sm">
                <span className="px-2 py-1 rounded-md bg-muted">{descricao || `Ativo #${valorId}`}</span>
                {!disabled && (
                    <button type="button" className="text-destructive font-bold" onClick={() => onChange(null, "")}>×</button>
                )}
            </div>
        );
    }

    return (
        <div className="relative">
            <input
                className={INPUT}
                value={termo}
                disabled={disabled}
                placeholder="Buscar por nomenclatura, patrimônio, série..."
                onChange={e => setTermo(e.target.value)}
            />
            {resultados.length > 0 && (
                <ul className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-auto rounded-md border border-border bg-card shadow-lg text-sm">
                    {resultados.map(r => (
                        <li
                            key={r.id}
                            className="px-3 py-2 cursor-pointer hover:bg-muted"
                            onClick={() => { onChange(r.id, r.descricao); setTermo(""); setResultados([]); }}
                        >
                            <strong>{r.descricao}</strong>
                            <span className="text-muted-foreground"> · {r.tipoDispositivoNome} · {r.statusNome}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function valoresIniciais(ativo) {
    return ativo ? { ...ativo } : {};
}

export default function AtivoFormModal({ visible, onHide, service, ativo, opcoes, onSalvo }) {
    const { metadados, catalogos = {}, filiais = [] } = opcoes;
    const edicao = !!ativo;

    const [valores, setValores] = useState({});
    const [motivo, setMotivo] = useState("");
    const [principalDescricao, setPrincipalDescricao] = useState("");
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (!visible) return;
        setValores(valoresIniciais(ativo));
        setPrincipalDescricao(ativo?.equipamentoPrincipalDescricao || "");
        setMotivo("");
    }, [visible, ativo]);

    const tipoSelecionado = (catalogos.TIPO_DISPOSITIVO || []).find(t => t.id === valores.tipoDispositivoId);
    const categoria = edicao ? ativo.categoria : tipoSelecionado?.categoria;

    const campos = useMemo(() => {
        const def = metadados?.categorias?.find(c => c.codigo === categoria);
        return (def?.campos || []).filter(c => c.chave !== "tipoDispositivoId");
    }, [metadados, categoria]);

    const tiposDisponiveis = opcoesDeCatalogo(
        catalogos.TIPO_DISPOSITIVO,
        valores.tipoDispositivoId,
        edicao ? (t => t.categoria === ativo.categoria) : null);

    const definir = (chave, valor) => {
        setValores(prev => {
            const proximo = { ...prev, [chave]: valor };
            if (chave === "marcaId") proximo.modeloId = null;
            if (chave === "tipoDispositivoId" && !proximo.tipoComputador) {
                const tipo = (catalogos.TIPO_DISPOSITIVO || []).find(t => t.id === valor);
                const sugerido = tipoComputadorSugerido(tipo?.nome);
                if (sugerido) proximo.tipoComputador = sugerido;
            }
            return proximo;
        });
    };

    const salvar = async () => {
        if (vazio(valores.tipoDispositivoId)) {
            toast.error("Selecione o tipo de dispositivo.");
            return;
        }
        const macInvalido = campos.filter(c => CAMPOS_MAC.includes(c.chave) && !macCompleto(valores[c.chave]));
        if (macInvalido.length) {
            toast.error(`Informe o MAC completo (AA:BB:CC:DD:EE:FF): ${macInvalido.map(c => c.rotulo).join(", ")}.`);
            return;
        }
        const faltando = campos
            .filter(c => c.obrigatorio && campoVisivel(c, valores) && vazio(valores[c.chave])
                && !(edicao && c.somenteMovimentacao))
            .map(c => c.rotulo);
        if (faltando.length) {
            toast.error(`Campos obrigatórios não informados: ${faltando.join(", ")}.`);
            return;
        }

        const payload = { tipoDispositivoId: valores.tipoDispositivoId, motivo: motivo || null };
        campos.forEach(c => {
            let v = valores[c.chave];
            if (!campoVisivel(c, valores) || vazio(v)) v = null;
            else if (c.tipo === "NUMERO_INTEIRO" || c.tipo === "DECIMAL") v = Number(v);
            payload[c.chave] = v;
        });

        setSalvando(true);
        try {
            const res = edicao
                ? await service.atualizarAtivo(ativo.id, payload)
                : await service.criarAtivo(payload);
            toast.success(edicao ? "Ativo atualizado com sucesso!" : "Ativo cadastrado com sucesso!");
            onSalvo?.(res.data);
            onHide();
        } catch (e) {
            // a mensagem de validação do backend já é exibida pelo interceptor do axios
        } finally {
            setSalvando(false);
        }
    };

    const controle = (def) => {
        const valor = valores[def.chave];
        if (edicao && def.somenteMovimentacao) {
            return (
                <div className="text-sm py-1.5">
                    {exibirCampo(ativo, def)}
                    <span className="block text-xs text-muted-foreground">Altere pela operação “Movimentar”.</span>
                </div>
            );
        }
        switch (def.tipo) {
            case "TEXTO_LONGO":
                return <textarea rows={3} className={INPUT} value={valor ?? ""} onChange={e => definir(def.chave, e.target.value)} />;
            case "NUMERO_INTEIRO":
                return <input type="number" min="0" className={INPUT} value={valor ?? ""} onChange={e => definir(def.chave, e.target.value)} />;
            case "DECIMAL":
                return <input type="number" min="0" step="0.01" className={INPUT} value={valor ?? ""} onChange={e => definir(def.chave, e.target.value)} />;
            case "DATA":
                return <input type="date" className={INPUT} value={valor ?? ""} onChange={e => definir(def.chave, e.target.value)} />;
            case "OPCAO":
                return (
                    <Dropdown
                        className="w-full text-sm"
                        value={valor ?? null}
                        options={(def.opcoes || []).map(o => ({ value: o.valor, label: o.rotulo }))}
                        onChange={e => definir(def.chave, e.value)}
                        placeholder="Selecione..."
                        showClear
                    />
                );
            case "CATALOGO": {
                const ehModelo = def.catalogo === "MODELO";
                const opcoesItem = opcoesDeCatalogo(catalogos[def.catalogo], valor,
                    ehModelo ? (i => i.paiId === valores.marcaId) : null);
                return (
                    <Dropdown
                        className="w-full text-sm"
                        value={valor ?? null}
                        options={opcoesItem}
                        onChange={e => definir(def.chave, e.value)}
                        placeholder={ehModelo && !valores.marcaId ? "Selecione a marca" : "Selecione..."}
                        disabled={ehModelo && !valores.marcaId}
                        filter
                        showClear
                    />
                );
            }
            case "FILIAL":
                return (
                    <Dropdown
                        className="w-full text-sm"
                        value={valor ?? null}
                        options={filiais.map(f => ({ value: f.id, label: f.nome }))}
                        onChange={e => definir(def.chave, e.value)}
                        placeholder="Selecione..."
                        filter
                    />
                );
            case "RESPONSAVEL":
                return (
                    <SeletorResponsavel
                        service={service}
                        nome={valores.responsavelNome}
                        cargo={valores.responsavelCargo}
                        onChange={({ nome, cargo }) => setValores(prev => ({ ...prev, responsavelNome: nome, responsavelCargo: cargo }))}
                    />
                );
            case "ATIVO":
                return (
                    <SeletorAtivo
                        service={service}
                        valorId={valor}
                        descricao={principalDescricao}
                        excluirId={ativo?.id}
                        onChange={(id, desc) => { definir(def.chave, id); setPrincipalDescricao(desc); }}
                    />
                );
            default:
                if (CAMPOS_MAC.includes(def.chave)) {
                    return (
                        <input
                            className={`${INPUT} font-mono`}
                            value={valor ?? ""}
                            inputMode="text"
                            autoComplete="off"
                            maxLength={17}
                            placeholder="AA:BB:CC:DD:EE:FF"
                            onChange={e => definir(def.chave, mascaraMac(e.target.value))}
                        />
                    );
                }
                return <input className={INPUT} value={valor ?? ""} onChange={e => definir(def.chave, e.target.value)} />;
        }
    };

    return (
        <Dialog
            header={edicao ? `Editar ativo — ${ativo.nomenclatura || ativo.patrimonio || `#${ativo.id}`}` : "Novo ativo de TI"}
            visible={visible}
            onHide={onHide}
            blockScroll
            className="w-[96%] md:w-[900px]"
            contentClassName="pt-3"
        >
            <div className="mb-4 max-w-sm">
                <label className="block text-xs text-muted-foreground mb-1">Tipo de dispositivo *</label>
                <Dropdown
                    className="w-full text-sm"
                    value={valores.tipoDispositivoId ?? null}
                    options={tiposDisponiveis}
                    onChange={e => definir("tipoDispositivoId", e.value)}
                    placeholder="Selecione o tipo de dispositivo"
                    filter
                />
                {categoria && (
                    <p className="text-xs text-muted-foreground mt-1">
                        Categoria: {metadados?.categorias?.find(c => c.codigo === categoria)?.rotulo}
                    </p>
                )}
            </div>

            {!categoria && (
                <p className="text-sm text-muted-foreground">
                    Escolha o tipo de dispositivo para exibir os campos da categoria correspondente.
                </p>
            )}

            {categoria && metadados?.secoes?.map(secao => {
                const doGrupo = campos.filter(c => c.secao === secao.codigo && campoVisivel(c, valores));
                if (doGrupo.length === 0) return null;
                return (
                    <div key={secao.codigo} className="mb-5">
                        <p className="text-sm font-semibold text-foreground border-b border-border pb-1 mb-3">{secao.rotulo}</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {doGrupo.map(def => (
                                <div key={def.chave} className={def.tipo === "TEXTO_LONGO" ? "md:col-span-3" : ""}>
                                    <label className="block text-xs text-muted-foreground mb-1">
                                        {def.rotulo}{def.obrigatorio ? " *" : ""}
                                    </label>
                                    {controle(def)}
                                    {AJUDA_CAMPOS[def.chave] && (
                                        <p className="text-[11px] text-muted-foreground mt-1">{AJUDA_CAMPOS[def.chave]}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}

            {categoria && (
                <div className="mb-2">
                    <label className="block text-xs text-muted-foreground mb-1">
                        {edicao ? "Motivo / observação da alteração (opcional)" : "Observação do cadastro (opcional)"}
                    </label>
                    <input className={INPUT} value={motivo} onChange={e => setMotivo(e.target.value)} />
                </div>
            )}

            <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-border">
                <Button label="Cancelar" variant="gray" onClick={onHide} />
                <Button label="Salvar" variant="indigo" loading={salvando} disabled={!categoria} onClick={salvar} />
            </div>
        </Dialog>
    );
}
